import type { Bi } from 'schools/kp/types/bilingual';

export type KpTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type KpChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: KpTopic[];
};

export const KP_HERO = {
  eyebrow: {
    en: 'Stellar precision · KP',
    hi: 'नक्षत्र सटीकता · KP',
  },
  titleShort: { en: 'KP Astrology', hi: 'KP ज्योतिष' },
  title: { en: 'Krishnamurti Paddhati', hi: 'कृष्णमूर्ति पद्धति' },
  motto: {
    en: 'Rule-driven precision: sub-lords, Placidus cusps, and exact event timing.',
    hi: 'नियमबद्ध सटीकता: उप-स्वामी, प्लैसिडस संधि, और सटीक घटना समय।',
  },
  tagline: {
    en: 'Master KP from Vedic foundations through 249 sub-lords, significator tables, house formulas, dasha-transit timing, horary Prashna, and live practicum.',
    hi: 'वैदिक आधार से 249 उप-स्वामी, कारक सारणी, भाव सूत्र, दशा-गोचर समय, प्रश्न और व्यावहारिक अभ्यास तक KP सीखें।',
  },
  intro: {
    en: 'Krishnamurti Paddhati (KP), shaped by Prof. K.S. Krishnamurti, is Vedic astrology made precise and rule-bound. It refines classical methods by dividing each nakshatra into nine unequal sub-divisions under sub-lords, then reads Placidus cusps with strict cuspal analysis - so answers can be a clear yes or no, and events can be timed with exactness. Shared graha and nakshatra art is sky identity only; KP still judges by cusps and Sub-Lords, not whole-sign Parashari house charts.',
    hi: 'कृष्णमूर्ति पद्धति (KP), प्रो. के. एस. कृष्णमूर्ति द्वारा रची गई, वैदिक ज्योतिष को सटीक और नियमबद्ध बनाती है। यह प्रत्येक नक्षत्र को उप-स्वामियों के अधीन नौ असमान उप-विभागों में बाँटकर पारंपरिक विधियों को परिष्कृत करती है, फिर प्लैसिडस संधियों का कठोर विश्लेषण करती है - ताकि उत्तर स्पष्ट हाँ या नहीं हो सकें, और घटनाओं का समय सटीक तय हो। साझा ग्रह-नक्षत्र चित्र आकाश पहचान मात्र हैं; KP निर्णय संधि और उप-स्वामी से करता है, पूर्ण-राशि पारंपरिक भाव चार्ट से नहीं।',
  },
  trait: {
    en: 'Sub-lord driven · cusp precise',
    hi: 'उप-स्वामी आधारित · संधि सटीक',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): KpTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const KP_CHAPTERS: KpChapter[] = [
  {
    id: 'foundations',
    number: 1,
    title: { en: 'KP Foundations', hi: 'KP आधार' },
    subtitle: {
      en: 'Zodiac, grahas, bhavas, nakshatras, and Vimshottari before KP mechanics',
      hi: 'KP यांत्रिकी से पहले राशि, ग्रह, भाव, नक्षत्र और विंशोत्तरी',
    },
    icon: '☉',
    topics: [
      topic('celestial-sphere-signs', 'Celestial Sphere & Signs', 'खगोलीय गोलक और राशियाँ', 'The 360° zodiac, ecliptic, twelve rashis, elements, and sign lords.', '360° राशि चक्र, क्रांतिवृत्त, बारह राशियाँ, तत्व और राशि स्वामी।', 20, '◎'),
      topic('nine-grahas', 'Nine Grahas', 'नौ ग्रह', 'Karakatwas, natural benefic/malefic nature, and planetary strengths.', 'कारकत्व, स्वाभाविक शुभ/अशुभ प्रकृति और ग्रह बल।', 18, '☉'),
      topic('twelve-bhavas', 'Twelve Bhavas', 'बारह भाव', 'House significations from self to liberation - the life map.', 'आत्मा से मोक्ष तक भाव अर्थ - जीवन का नक्शा।', 18, '⌂'),
      topic('twenty-seven-nakshatras', '27 Nakshatras', '27 नक्षत्र', 'Names, spans of 13°20′, and Vimshottari lords of each star.', 'नाम, 13°20′ विस्तार और प्रत्येक नक्षत्र के दशा स्वामी।', 19, '✦'),
      topic('vimshottari-dasha', 'Vimshottari Dasha', 'विंशोत्तरी दशा', 'The 120-year cycle: Mahadasha, Antardasha, and Pratyantar structure.', '120 वर्ष चक्र: महादशा, अंतर्दशा और प्रत्यंतर संरचना।', 20, '⏳'),
    ],
  },
  {
    id: 'kp-shift',
    number: 2,
    title: { en: 'Vedic Transition', hi: 'वैदिक संक्रमण' },
    subtitle: {
      en: 'Why KP replaces sign-based houses with cuspal sub-lord logic',
      hi: 'KP भाव-राशि को संधि उप-स्वामी तर्क से क्यों बदलता है',
    },
    icon: '⇄',
    topics: [
      topic('pitfalls-traditional', 'Traditional Pitfalls', 'पारंपरिक सीमाएँ', 'Why whole-sign and vague timing fail for precise events.', 'पूर्ण राशि और अस्पष्ट समय सटीक घटनाओं में क्यों विफल होते हैं।', 17, '⚠'),
      topic('placidus-cusps', 'Placidus Cusps', 'प्लैसिडस संधि', 'KP house construction from exact mathematical cusp degrees.', 'सटीक गणितीय संधि अंश से KP भाव निर्माण।', 19, '⊞'),
      topic('intercepted-houses', 'Intercepted Houses', 'अंतर्गत भाव', 'When a sign is swallowed inside a house or a house spans multiple signs.', 'जब राशि भाव में दबी हो या भाव अनेक राशियों में फैला हो।', 16, '↔'),
      topic('kp-ayanamsha', 'KP Ayanamsha', 'KP अयनांश', 'Krishnamurti ayanamsha and its role in degree-level accuracy.', 'कृष्णमूर्ति अयनांश और अंश-स्तर सटीकता में भूमिका।', 18, '°'),
      topic('kp-golden-rule', 'KP Golden Rule', 'KP स्वर्ण नियम', 'Results from Star Lord; flavour decided by Sub-Lord.', 'फल तारा स्वामी से; स्वाद उप-स्वामी तय करता है।', 20, '★'),
    ],
  },
  {
    id: 'sub-lord',
    number: 3,
    title: { en: 'Sub-Lord Core', hi: 'उप-स्वामी मूल' },
    subtitle: {
      en: 'The 249 divisions and table construction that power KP precision',
      hi: '249 विभाजन और सारणी निर्माण जो KP सटीकता देते हैं',
    },
    icon: '⊛',
    topics: [
      topic('249-sub-divisions', '249 Sub-Divisions', '249 उप-विभाजन', 'Nine unequal sub-lords per nakshatra from Vimshottari year proportions.', 'विंशोत्तरी वर्ष अनुपात से नक्षत्र में नौ असमान उप-स्वामी।', 22, '⊕'),
      topic('kp-table-construction', 'KP Table Build', 'KP सारणी निर्माण', 'Mapping all 249 zones across signs, stars, and subs.', 'राशि, नक्षत्र और उप में सभी 249 क्षेत्र मानचित्रण।', 20, '▦'),
      topic('sign-star-sub-id', 'Sign Star Sub ID', 'राशि तारा उप पहचान', 'Finding Sign Lord, Star Lord, and Sub-Lord for any degree.', 'किसी भी अंश के लिए राशि, तारा और उप स्वामी ज्ञात करना।', 19, '⌖'),
      topic('cuspal-planetary-subs', 'Cuspal vs Planetary', 'संधि बनाम ग्रह', 'House promise (cuspal sub) versus planet delivery (planetary sub).', 'भाव वादा (संधि उप) बनाम ग्रह वितरण (ग्रह उप)।', 18, '⇌'),
      topic('kp-software-tools', 'KP Software Tools', 'KP सॉफ्टवेयर', 'Using chart software to avoid manual Placidus math - informational overview.', 'मैनुअल प्लैसिडस गणित से बचने हेतु कुंडली सॉफ्टवेयर - सूचनात्मक परिचय।', 16, '💻'),
    ],
  },
  {
    id: 'significators',
    number: 4,
    title: { en: 'Significator Script', hi: 'कारक लेखन' },
    subtitle: {
      en: 'Four levels of planetary authority and the significator table',
      hi: 'ग्रह अधिकार के चार स्तर और कारक सारणी',
    },
    icon: '≋',
    topics: [
      topic('four-signification-levels', 'Four Signification Levels', 'चार कारक स्तर', 'Occupant in star of occupant through house ownership hierarchy.', 'अधिवासी की तारा से स्वामित्व पदानुक्रम तक चार स्तर।', 20, '④'),
      topic('strong-weak-significators', 'Strong vs Weak', 'बलवान बनाम दुर्बल', 'Ranking planets by authority to deliver a house result.', 'भाव फल देने की अधिकार श्रेणी में ग्रहों की रैंकिंग।', 18, '⚖'),
      topic('significator-table', 'Significator Table', 'कारक सारणी', 'Building and reading the standard KP planetary and cuspal tables.', 'मानक KP ग्रह और संधि कारक सारणी बनाना और पढ़ना।', 19, '▤'),
    ],
  },
  {
    id: 'event-formulas',
    number: 5,
    title: { en: 'Event Formulas', hi: 'घटना सूत्र' },
    subtitle: {
      en: 'House grouping, negation rule, and binary formulas for life events',
      hi: 'भाव समूह, निषेध नियम और जीवन घटनाओं के द्विगुण सूत्र',
    },
    icon: '∑',
    topics: [
      topic('scripting-planet', 'Scripting Planets', 'ग्रह लेखन', 'Writing Planet → Star Lord → Sub-Lord equations.', 'ग्रह → तारा स्वामी → उप-स्वामी समीकरण लिखना।', 17, '✎'),
      topic('twelfth-house-negation', '12th House Negation', '12वें भाव निषेध', 'Why the house before any bhava negates its fruit.', 'किसी भाव से पहले वाला भाव उसके फल का निषेध क्यों करता है।', 18, '⊖'),
      topic('formulas-education-career', 'Education & Career', 'शिक्षा और करियर', 'Houses 4-9-11 and 2-6-10-11 with negation sets.', 'भाव 4-9-11 और 2-6-10-11 तथा निषेध समूह।', 20, '🎓'),
      topic('formulas-marriage-health', 'Marriage & Health', 'विवाह और स्वास्थ्य', 'Houses 2-7-11, 1-6-8-12, plus property and foreign travel sets.', 'भाव 2-7-11, 1-6-8-12, संपत्ति और विदेश यात्रा समूह।', 20, '♥'),
    ],
  },
  {
    id: 'timing',
    number: 6,
    title: { en: 'Event Timing', hi: 'घटना समय' },
    subtitle: {
      en: 'Dasha co-rulers, fruitful planets, and Sun-Moon-Jupiter transits',
      hi: 'दशा सह-स्वामी, फलदायी ग्रह और सूर्य-चंद्र-गुरु गोचर',
    },
    icon: '⏱',
    topics: [
      topic('dasha-co-rulers', 'Dasha Co-Rulers', 'दशा सह-स्वामी', 'Mahadasha, Antardasha, Pratyantar significators for the event.', 'घटना हेतु महादशा, अंतर्दशा, प्रत्यंतर कारक।', 19, '◎'),
      topic('fruitful-obstructive', 'Fruitful Planets', 'फलदायी ग्रह', 'Filtering obstacles and detrimental sub-lords from the promise.', 'वादे से बाधक और हानिकर उप-स्वामी छाँटना।', 18, '✓'),
      topic('transit-triggers', 'Transit Triggers', 'गोचर ट्रिगर', 'Sun for month, Moon for day, Jupiter as supporting trigger.', 'महीने हेतु सूर्य, दिन हेतु चंद्र, सहायक ट्रिगर गुरु।', 20, '☽'),
    ],
  },
  {
    id: 'horary',
    number: 7,
    title: { en: 'KP Prashna', hi: 'KP प्रश्न' },
    subtitle: {
      en: 'Horary astrology when birth data is unavailable',
      hi: 'जन्म विवरण न होने पर प्रश्न ज्योतिष',
    },
    icon: '?',
    topics: [
      topic('prashna-philosophy', 'Prashna Philosophy', 'प्रश्न दर्शन', 'Mind-query link and the moment of sincere questioning.', 'मन-प्रश्न संबंध और सच्ची पूछताछ का क्षण।', 17, '☯'),
      topic('one-to-249-system', '1–249 Number System', '1–249 संख्या पद्धति', 'How the querent number locks the ascendant cusp degree.', 'प्रश्नकर्ता संख्या से लग्न संधि अंश कैसे तय होता है।', 19, '#'),
      topic('query-genuineness', 'Query Genuineness', 'प्रश्न सत्यता', 'Moon as mind: testing if the chart reflects true intent.', 'चंद्र मन के रूप में: कुंडली वास्तविक संकल्प दर्खाती है या नहीं।', 18, '☽'),
      topic('judging-prashna', 'Judging Prashna', 'प्रश्न निर्णय', 'Step-by-step resolution of sharp direct questions.', 'तीक्ष्ण प्रत्यक्ष प्रश्नों का चरणबद्ध निराकरण।', 20, '⚡'),
    ],
  },
  {
    id: 'practicum',
    number: 8,
    title: { en: 'KP Practicum', hi: 'KP अभ्यास' },
    subtitle: {
      en: 'Sub-lord calculation drill, ruling planets, birth-time rectification, case studies, and ethics',
      hi: 'उप-स्वामी गणना अभ्यास, शासक ग्रह, जन्म समय शोध, केस अध्ययन और नैतिकता',
    },
    icon: '◈',
    topics: [
      topic('ruling-planets', 'Ruling Planets', 'शासक ग्रह', 'Five core RPs and their use in dilemmas and confirmation.', 'पाँच मुख्य RP और दुविधा/पुष्टि में उपयोग।', 19, '☉'),
      topic('birth-time-rectification', 'Birth Time Rectification', 'जन्म समय शोध', 'Using RPs to correct inaccurate birth time to the second.', 'अशुद्ध जन्म समय को सेकंड तक RP से सुधारना।', 20, '⌚'),
      topic('case-study-method', 'Case Study Method', 'केस अध्ययन विधि', 'Marriage, job, wealth charts - live decoding drills.', 'विवाह, नौकरी, धन कुंडली - सजीव विश्लेषण अभ्यास।', 22, '🔍'),
      topic('ethics-counselling', 'Ethics & Counselling', 'नैतिकता और परामर्श', 'Delivering precise predictions with constructive professional tone.', 'रचनात्मक व्यावसायिक स्वर में सटीक फलित देना।', 17, '⚖'),
      topic(
        'how-sub-lord-calculated',
        'How a KP Sub-Lord Is Calculated',
        'KP उप-स्वामी कैसे गणना होता है',
        'From exact cusp degree → Nakshatra → Star Lord (NL) → Sub-Lord (SL) → Sub-Sub Lord (SS).',
        'सटीक संधि अंश → नक्षत्र → तारा स्वामी (NL) → उप-स्वामी (SL) → उप-उप स्वामी (SS)।',
        22,
        '⊕',
      ),
    ],
  },
];

const topicById = new Map<string, { topic: KpTopic; chapter: KpChapter }>();

for (const chapter of KP_CHAPTERS) {
  for (const t of chapter.topics) {
    topicById.set(t.id, { topic: t, chapter });
  }
}

export function getKpTopic(id: string) {
  return topicById.get(id);
}

export function getKpChapter(chapterId: string): KpChapter | undefined {
  return KP_CHAPTERS.find((c) => c.id === chapterId);
}

export function getKpChapterIndex(chapterId: string): number {
  return KP_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getKpChapterNeighbors(chapterId: string): {
  prev: KpChapter | null;
  next: KpChapter | null;
} {
  const idx = getKpChapterIndex(chapterId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? KP_CHAPTERS[idx - 1] : null,
    next: idx < KP_CHAPTERS.length - 1 ? KP_CHAPTERS[idx + 1] : null,
  };
}

export function getKpChapterReadMinutes(chapter: KpChapter): number {
  return chapter.topics.reduce((sum, t) => sum + t.readMinutes, 0);
}

export function getAllKpTopics(): KpTopic[] {
  return KP_CHAPTERS.flatMap((c) => c.topics);
}

export function getKpTopicCount(): number {
  return getAllKpTopics().length;
}

const flatTopicOrder = KP_CHAPTERS.flatMap((c) => c.topics);

export function getKpTopicNeighbors(topicId: string): {
  prev: KpTopic | null;
  next: KpTopic | null;
} {
  const idx = flatTopicOrder.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flatTopicOrder[idx - 1] : null,
    next: idx < flatTopicOrder.length - 1 ? flatTopicOrder[idx + 1] : null,
  };
}
